import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import '../Login.css';  // Ensure the CSS is tailored for styling the login page

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const extractLoginErrorMessage = (error) => {
  const apiMessage = error?.response?.data?.error
    || error?.response?.data?.detail
    || error?.response?.data?.message;
  if (typeof apiMessage === 'string' && apiMessage.trim()) {
    return apiMessage;
  }

  if (error?.request && !error?.response) {
    return `Unable to reach backend at ${apiUrl('token/')}.`;
  }

  return 'Login failed. Please check your credentials.';
};

const Login = ({ setIsAuthenticated, setUserRole, setNotification }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    const loginData = { username, password };

    try {
      // Send login request to get JWT token
      const response = await axios.post(apiUrl('token/'), loginData);
      console.log("Token Response:", response.data);

      // Store the JWT token in local storage
      localStorage.setItem('token', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refreshToken', response.data.refresh);
      }
      window.dispatchEvent(new Event('auth-changed'));
      setMessage('Login successful!');
      setIsAuthenticated(true); // Update authentication state in App.js
      console.log('Login successful, isAuthenticated set to true');

      console.log("Storing token:", response.data.access);
      const token = localStorage.getItem('token');
      console.log("Stored Token:", token);

      // Fetch user profile to check the role
      const userProfileResponse = await axios.get(apiUrl('userprofiles/me/'), {
      headers: { Authorization: `Bearer ${response.data.access}` },
      });
      console.log("User Role:", userProfileResponse.data.role);

      const userRole = userProfileResponse.data.role;
      localStorage.setItem('userRole', userRole);
      setUserRole?.(userRole);
      setNotification?.({ message: 'Login successful!', type: 'success' });

      // Navigate based on user role
      if (userRole === 'client') {
        // Redirect client to the previous page or home if no previous page is provided
        console.log("Navigating to homepage:", userProfileResponse.data.role);
        navigate(location.state?.from || '/');
      } else if (userRole === 'admin') {
        // Redirect admins to the admin dashboard
        console.log("Navigating to dashboard:", userProfileResponse.data.role);
        navigate('/dashboard');
      } else if (userRole === 'worker') {
        // Redirect workers to a dedicated worker dashboard
        console.log("Navigating to worker dashboard:", userProfileResponse.data.role);
        navigate('/worker-dashboard');
      } else if (userRole === 'owner') {
        console.log("Navigating to owner dashboard:", userProfileResponse.data.role);
        navigate('/owner-dashboard');
      } else {
        // Fallback for unknown roles
        navigate('/');
      }
    } catch (error) {
      console.error('Login failed', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userRole');
      window.dispatchEvent(new Event('auth-changed'));
      setIsAuthenticated(false);
      setUserRole?.(null);
      const loginErrorMessage = extractLoginErrorMessage(error);
      setNotification?.({ message: loginErrorMessage, type: 'error' });
      setMessage(loginErrorMessage);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h2>Login to Your Account</h2>
        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              type="text"
              id="username"
              className="form-control"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <div className="login-password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                <span aria-hidden="true">{showPassword ? '🙈' : '👁️'}</span>
              </button>
            </div>
          </div>
          <button type="submit" className="btn-primary">Login</button>
        </form>
        {message && <p className="error-message">{message}</p>}

        {/* Sign-Up Option for Clients */}
        <div className="signup-options">
          <p>Don't have an account?</p>
          <Link to="/signup-client" className="btn-secondary">Sign Up</Link>
          <div className="mt-3 flex flex-col gap-2">
            <Link to="/signup-owner" className="btn-secondary">Apply as Owner</Link>
            <Link to="/signup-worker" className="btn-secondary">Apply as Worker</Link>
          </div>
          <p className="mt-3 text-sm text-gray-600">
            You can apply directly as owner or worker without creating a client account first.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
